import { Box, Collapse, IconButton } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { EntityTemplateField } from "../../entityTemplates/EntityTemplate.types.ts";
import { ContractModel } from "../contract/Contract.types.ts";
import AutoRenewalDurationField from "../contract/fields/AutoRenewalDurationField.tsx";
import { isAutoRenewalShown } from "../contract/fields/AutoRenewalDurationField.utils.ts";
import ContractAiUpdateFieldWrapper from "./ContractAiUpdateFieldWrapper.tsx";
import { useContractAiUpdateStyles } from "./ContractAiUpdate.styles.ts";

export interface ContractAiUpdateAutoRenewalRowProps {
    endDateField: EntityTemplateField;
    currentContract: ContractModel;
    proposedContract: ContractModel;
    canRevert: boolean;
    onProposedChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    onRevert: () => void;
}

/**
 * The end date's renewal period on a row of its own, so it gets an arrow of its own: inside the end date's
 * row, that row's arrow would have to choose for both. Each column shows the period only where its own end
 * date is set, as the end date field does.
 */
function ContractAiUpdateAutoRenewalRow({
    endDateField,
    currentContract,
    proposedContract,
    canRevert,
    onProposedChange,
    onRevert,
}: ContractAiUpdateAutoRenewalRowProps) {
    const { translateString } = useTranslation();
    const styles = useContractAiUpdateStyles();

    return (
        <Box sx={styles.row}>
            <Box sx={{ ...styles.column, ...styles.readOnlyColumn }}>
                <Collapse in={isAutoRenewalShown(endDateField, currentContract.endDate)}>
                    <ContractAiUpdateFieldWrapper>
                        <AutoRenewalDurationField
                            autoRenewalDuration={currentContract.autoRenewalDuration}
                            autoRenewalDurationType={currentContract.autoRenewalDurationType}
                            onChange={async () => {}}
                            readOnly
                            disabled
                            hideToolTip
                            noMargin
                        />
                    </ContractAiUpdateFieldWrapper>
                </Collapse>
            </Box>

            <Box sx={styles.arrowCell}>
                {canRevert && (
                    <DotLegalTooltip text={translateString("useCurrentValue")}>
                        <IconButton size="small" onClick={onRevert}>
                            <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                    </DotLegalTooltip>
                )}
            </Box>

            <Box sx={styles.column}>
                <Collapse in={isAutoRenewalShown(endDateField, proposedContract.endDate)}>
                    <ContractAiUpdateFieldWrapper>
                        <AutoRenewalDurationField
                            autoRenewalDuration={proposedContract.autoRenewalDuration}
                            autoRenewalDurationType={proposedContract.autoRenewalDurationType}
                            onChange={onProposedChange}
                            readOnly={false}
                            disabled={false}
                            hideToolTip
                            noMargin
                        />
                    </ContractAiUpdateFieldWrapper>
                </Collapse>
            </Box>
        </Box>
    );
}

export default ContractAiUpdateAutoRenewalRow;
